import React, { useState } from 'react';
import {
    X, ChevronDown, Calendar, MapPin, LayoutList,
    DollarSign, TrendingUp, TrendingDown, AlertCircle, Eye, Printer, Mail,
    User, Smartphone, Phone
} from 'lucide-react';
import './CommonStyles.css';
import './MobileResponsive.css'; // Import mobile responsive styles
import ViewPaymentsModal from './ViewPaymentsModal';
import AddPaymentModal from './AddPaymentModal';
import apiService from '../../services/api';

// ─── Dummy Data ───────────────────────────────────────────────────────────────
const generateDummyLedger = (sale, currentContact) => {
    // Determine dynamic values from the 'sale' object or selected contact
    // Matching AllSell.jsx property names: invoiceNo, finalTotal, sellDue, totalPaid
    const invNo = sale?.invoiceNo || sale?.invoice || '84_2025422711';

    // Total from sale or default (finalTotal)
    const totalInv = parseFloat(sale?.finalTotal || sale?.totalPayable || sale?.total || 60.48);

    // Due from sale (sellDue)
    const balDue = parseFloat(sale?.sellDue !== undefined ? sale.sellDue : (sale?.due !== undefined ? sale.due : 60.48));

    // Paid from sale or calculation (totalPaid)
    const totPaid = sale?.totalPaid !== undefined ? parseFloat(sale.totalPaid) : (totalInv - balDue);

    const loc = sale?.location || "Lefty'S Liquor";

    // Use selected contact info if available, otherwise fallback to sale/dummy
    const cust = currentContact?.name || sale?.customer || 'Dr Rahim B';
    const addr = currentContact?.address_line_1
        ? `${currentContact.address_line_1}\n${currentContact.city || ''}, ${currentContact.state || ''}, ${currentContact.country || ''}`
        : 'Flat 402, Green Residency,\nBandra West,\nMumbai, Maharashtra, India,\n400050';

    return {
        customer: cust,
        address: addr,
        email: currentContact?.email || 'anjali.patel@example.com',
        mobile: currentContact?.mobile || '9988776655',
        landline: currentContact?.landline || '0223344556',
        alternateContact: currentContact?.alternate_number || '9123456789',
        dob: currentContact?.dob || '05/20/1988',
        customField1: currentContact?.custom_field1 || 'Preferred Customer',
        customField2: currentContact?.custom_field2 || 'Loyalty Card 9988',
        customField3: currentContact?.custom_field3 || 'Referred by Rajesh',
        customField4: currentContact?.custom_field4 || 'Special Discount',
        taxNumber: currentContact?.tax_number || 'PAN12345K',
        storeName: loc,
        storeAddress: '14199 E HIGHWAY 12 ROGERS,, Little Rock, AR\nUSA, 72756-8',
        invoiceNo: invNo,
        openingBalance: 0.00,
        totalInvoice: totalInv,
        totalPaid: totPaid,
        advanceBalance: 0.00,
        balanceDue: balDue,

        // Format 1 — Detailed Ledger
        detailedRows: [
            { date: sale?.saleDate || '2026-01-01', refNo: invNo, type: 'Sale', location: loc, status: sale?.paymentStatus || 'Partial', debit: totalInv, credit: 0, balance: totalInv, method: '', others: '' },
            { date: '2026-01-05', refNo: 'PAY-0012', type: 'Payment', location: loc, status: 'Paid', debit: 0, credit: totPaid, balance: balDue, method: 'Cash', others: '' },
        ],

        // Format 2 — Statement View
        statementRows: [
            { date: sale?.saleDate || '2026-01-01', transaction: `Invoice ${invNo}`, amount: totalInv, balance: totalInv, type: 'debit' },
            // Always show Payment row so "View Payment" is available for demo
            { date: '2026-01-05', transaction: 'Payment Received', amount: totPaid, balance: balDue, type: 'credit', hasViewPayment: true },
        ],

        // Format 3 — Detailed Transactions + Products
        transactions: [
            {
                date: sale?.saleDate || '2026-01-01',
                refNo: invNo,
                type: 'Sale',
                total: totalInv,
                products: [
                    { name: 'Product A', sku: 'SKU-001', qty: 2, unitPrice: (totalInv / 2), discount: 0.00, tax: 0.00, subtotal: totalInv },
                ]
            }
        ]
    };
};

// ─── Shared UI Components ─────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
    // Normalize status to capitalize first letter (e.g. "paid" -> "Paid")
    const normalizedStatus = status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';

    const map = {
        Paid: { bg: '#dcfce7', color: '#16a34a' },
        Partial: { bg: '#fef9c3', color: '#ca8a04' },
        Due: { bg: '#fee2e2', color: '#dc2626' },
        Sale: { bg: '#dbeafe', color: '#2563eb' },
        Payment: { bg: '#ede9fe', color: '#7c3aed' },
    };
    const style = map[normalizedStatus] || { bg: '#f1f5f9', color: '#64748b' };
    return (
        <span style={{ background: style.bg, color: style.color, borderRadius: '20px', padding: '0.2rem 0.65rem', fontSize: '0.75rem', fontWeight: '700' }}>
            {normalizedStatus || status}
        </span>
    );
};

// ─── Format 1: Detailed Ledger ────────────────────────────────────────────────
const Format1Table = ({ rows }) => (
    <div style={{ overflowX: 'auto', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    {['Date', 'Reference No', 'Type', 'Location', 'Payment Status', 'Debit', 'Credit', 'Balance', 'Payment Method', 'Others'].map(h => (
                        <th key={h} style={{ padding: '0.85rem 1rem', textAlign: 'left', color: '#1e293b', fontWeight: '700', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {/* Opening Balance Row */}
                <tr style={{ borderBottom: '1px solid #f1f5f9', background: '#fafafa' }}>
                    <td style={{ padding: '0.85rem 1rem', color: '#64748b' }}>01/01/2026</td>
                    <td style={{ padding: '0.85rem 1rem' }}></td>
                    <td style={{ padding: '0.85rem 1rem', color: '#334155', fontWeight: '600' }}>Opening</td>
                    <td colSpan={2}></td>
                    <td style={{ padding: '0.85rem 1rem', color: '#64748b' }}>$0.00</td>
                    <td colSpan={4}></td>
                </tr>
                {rows.map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: '#fff' }}>
                        <td style={{ padding: '0.85rem 1rem', color: '#64748b', whiteSpace: 'nowrap' }}>{row.date}</td>
                        <td style={{ padding: '0.85rem 1rem', color: '#6366f1', fontWeight: '600' }}>{row.refNo}</td>
                        <td style={{ padding: '0.85rem 1rem' }}><StatusBadge status={row.type} /></td>
                        <td style={{ padding: '0.85rem 1rem', color: '#64748b' }}>{row.location}</td>
                        <td style={{ padding: '0.85rem 1rem' }}><StatusBadge status={row.status} /></td>
                        <td style={{ padding: '0.85rem 1rem', color: row.debit > 0 ? '#dc2626' : '#94a3b8', fontWeight: '600' }}>
                            {row.debit > 0 ? `$${row.debit.toFixed(2)}` : '—'}
                        </td>
                        <td style={{ padding: '0.85rem 1rem', color: row.credit > 0 ? '#16a34a' : '#94a3b8', fontWeight: '600' }}>
                            {row.credit > 0 ? `$${row.credit.toFixed(2)}` : '—'}
                        </td>
                        <td style={{ padding: '0.85rem 1rem', color: '#1e293b', fontWeight: '700' }}>${row.balance.toFixed(2)}</td>
                        <td style={{ padding: '0.85rem 1rem', color: '#64748b' }}>{row.method}</td>
                        <td style={{ padding: '0.85rem 1rem', color: '#64748b' }}>{row.others}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

// ─── Format 2: Statement View ─────────────────────────────────────────────────
const Format2Table = ({ rows, onViewPayment }) => (
    <div style={{ overflowX: 'auto', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    {['Date', 'Transaction', 'Amount', 'Balance', 'Action'].map(h => (
                        <th key={h} style={{ padding: '0.85rem 1rem', textAlign: 'left', color: '#1e293b', fontWeight: '700' }}>{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: '#fff' }}>
                        <td style={{ padding: '0.85rem 1rem', color: '#64748b', whiteSpace: 'nowrap' }}>{row.date}</td>
                        <td style={{ padding: '0.85rem 1rem', color: '#334155', fontWeight: '600' }}>{row.transaction}</td>
                        <td style={{ padding: '0.85rem 1rem', color: row.type === 'credit' ? '#16a34a' : '#dc2626', fontWeight: '700' }}>
                            {row.type === 'credit' ? '+' : '-'}${row.amount.toFixed(2)}
                        </td>
                        <td style={{ padding: '0.85rem 1rem', color: '#1e293b', fontWeight: '700' }}>${row.balance.toFixed(2)}</td>
                        <td style={{ padding: '0.85rem 1rem' }}>
                            {row.hasViewPayment && (
                                <button onClick={() => onViewPayment && onViewPayment(row)} style={{ background: '#e0e7ff', color: '#4f46e5', border: '1px solid #c7d2fe', borderRadius: '6px', padding: '0.3rem 0.75rem', fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                    <Eye size={13} /> View Payment
                                </button>
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

// ─── Format 3: Detailed Transaction ──────────────────────────────────────────
const Format3Table = ({ transactions }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {transactions.map((txn, ti) => (
            <div key={ti} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
                {/* Transaction header */}
                <div style={{ background: txn.type === 'Sale' ? '#eff6ff' : '#f5f3ff', padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: txn.products?.length ? '1px solid #e2e8f0' : 'none' }}>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <StatusBadge status={txn.type} />
                        <span style={{ fontWeight: '700', color: '#4f46e5', fontSize: '0.9rem' }}>{txn.refNo}</span>
                        <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{txn.date}</span>
                    </div>
                    <span style={{ fontWeight: '800', color: '#1e293b', fontSize: '1.05rem' }}>${txn.total.toFixed(2)}</span>
                </div>

                {/* Product sub-table */}
                {txn.products?.length > 0 && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                {['Product Name', 'SKU', 'Qty', 'Unit Price', 'Discount', 'Tax', 'Subtotal'].map(h => (
                                    <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#64748b', fontWeight: '600', fontSize: '0.8rem' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {txn.products.map((p, pi) => (
                                <tr key={pi} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '0.75rem 1rem', color: '#334155', fontWeight: '600' }}>{p.name}</td>
                                    <td style={{ padding: '0.75rem 1rem', color: '#64748b' }}>{p.sku}</td>
                                    <td style={{ padding: '0.75rem 1rem', color: '#334155' }}>{p.qty}</td>
                                    <td style={{ padding: '0.75rem 1rem', color: '#334155' }}>${p.unitPrice.toFixed(2)}</td>
                                    <td style={{ padding: '0.75rem 1rem', color: '#dc2626' }}>${p.discount.toFixed(2)}</td>
                                    <td style={{ padding: '0.75rem 1rem', color: '#f59e0b' }}>${p.tax.toFixed(2)}</td>
                                    <td style={{ padding: '0.75rem 1rem', color: '#16a34a', fontWeight: '700' }}>${p.subtotal.toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        ))}
    </div>
);

// ─── Main LedgerModal ─────────────────────────────────────────────────────────
const LedgerModal = ({ sale, onClose }) => {
    const [activeTab, setActiveTab] = useState('Ledger');
    const [ledgerFormat, setLedgerFormat] = useState('Format 1');
    const [dateFrom, setDateFrom] = useState('2026-01-01');
    const [dateTo, setDateTo] = useState('2026-12-31');
    const [location, setLocation] = useState('all');

    // Dynamic Contact State
    const [contacts, setContacts] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const [isLoadingContacts, setIsLoadingContacts] = useState(false);

    // View Payment Modal State
    const [showViewPaymentsModal, setShowViewPaymentsModal] = useState(false);
    const [selectedPaymentRow, setSelectedPaymentRow] = useState(null);

    // Add Payment Modal State
    const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);

    React.useEffect(() => {
        const fetchContacts = async () => {
            setIsLoadingContacts(true);
            try {
                const response = await apiService.getContacts();
                if (response && response.data) {
                    setContacts(response.data);

                    // Try to match selected contact with initial sale
                    if (sale?.contact_id) {
                        const match = response.data.find(c => c.id === sale.contact_id);
                        if (match) setSelectedContact(match);
                    } else if (sale?.customer) {
                        const match = response.data.find(c => c.name === sale.customer);
                        if (match) setSelectedContact(match);
                    }
                }
            } catch (error) {
                console.error('Error fetching contacts for ledger:', error);
            } finally {
                setIsLoadingContacts(false);
            }
        };
        fetchContacts();
    }, [sale]);

    const handleViewPayment = (row) => {
        setSelectedPaymentRow(row);
        setShowViewPaymentsModal(true);
    };

    const data = generateDummyLedger(sale, selectedContact);

    const tabsList = ['Ledger', 'Sales', 'Documents & Note', 'Payments', "Lefty's Rewards", 'Activities', 'Contact Persons'];

    return (
        <div className="modal-overlay ledger-modal-overlay">
            {/* Modal Body */}
            <div className="modal-container ledger-modal-container dark-mode-modal">

                {/* ── View Contact Header ── */}
                <div className="ledger-header">
                    <h2 className="ledger-title">View Contact</h2>
                    <div className="ledger-header-actions">
                        <div className="ledger-contact-select-wrapper">
                            <select
                                value={selectedContact?.id || ''}
                                onChange={(e) => {
                                    const contact = contacts.find(c => String(c.id) === e.target.value);
                                    setSelectedContact(contact);
                                }}
                                className="ledger-contact-select"
                            >
                                {isLoadingContacts ? (
                                    <option>Loading...</option>
                                ) : (
                                    contacts.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.name} {c.contact_id ? `- (${c.contact_id})` : ''}
                                        </option>
                                    ))
                                )}
                                {contacts.length === 0 && !isLoadingContacts && (
                                    <option value="">{sale?.customer || 'Select Contact'}</option>
                                )}
                            </select>
                        </div>
                        <button onClick={onClose} className="ledger-close-btn" aria-label="Close">
                            <X size={18} strokeWidth={2.5} />
                        </button>
                    </div>
                </div>

                {/* ── Contact Details Card ── */}
                <div className="ledger-contact-card">
                    <div className="ledger-contact-grid">

                        {/* Left Col */}
                        <div className="ledger-contact-left">
                            <div className="ledger-contact-profile">
                                <div className="ledger-profile-icon">
                                    <User size={28} color="#334155" />
                                </div>
                                <div className="ledger-profile-info">
                                    <h3 className="ledger-customer-name">{data.customer}</h3>
                                    <span className="ledger-customer-label">Customer</span>
                                </div>
                            </div>

                            <div className="ledger-contact-fields">
                                <div className="ledger-field-item">
                                    <MapPin className="ledger-field-icon" size={18} />
                                    <div className="ledger-field-content">
                                        <strong className="ledger-field-label">Address</strong>
                                        <div className="ledger-field-value address-value">{data.address}</div>
                                    </div>
                                </div>
                                <div className="ledger-field-item">
                                    <Smartphone className="ledger-field-icon" size={18} />
                                    <div className="ledger-field-content">
                                        <strong className="ledger-field-label">Mobile</strong>
                                        <div className="ledger-field-value">{data.mobile}</div>
                                    </div>
                                </div>
                                <div className="ledger-field-item">
                                    <Phone className="ledger-field-icon" size={18} />
                                    <div className="ledger-field-content">
                                        <strong className="ledger-field-label">Landline</strong>
                                        <div className="ledger-field-value">{data.landline}</div>
                                    </div>
                                </div>
                                <div className="ledger-field-item">
                                    <Phone className="ledger-field-icon alternate-icon" size={18} />
                                    <div className="ledger-field-content">
                                        <strong className="ledger-field-label">Alternate contact</strong>
                                        <div className="ledger-field-value">{data.alternateContact}</div>
                                    </div>
                                </div>
                                <div className="ledger-field-item">
                                    <Calendar className="ledger-field-icon" size={18} />
                                    <div className="ledger-field-content">
                                        <strong className="ledger-field-label">Date of birth</strong>
                                        <div className="ledger-field-value">{data.dob}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Col */}
                        <div className="ledger-contact-right">
                            <div className="ledger-custom-fields">
                                <div className="ledger-custom-field">
                                    <strong className="ledger-field-label">Custom Field 1</strong>
                                    <div className="ledger-field-value">{data.customField1}</div>
                                </div>
                                <div className="ledger-custom-field">
                                    <strong className="ledger-field-label">Custom Field 2</strong>
                                    <div className="ledger-field-value">{data.customField2}</div>
                                </div>
                                <div className="ledger-custom-field">
                                    <strong className="ledger-field-label">Custom Field 3</strong>
                                    <div className="ledger-field-value">{data.customField3}</div>
                                </div>
                                <div className="ledger-custom-field">
                                    <strong className="ledger-field-label">Custom Field 4</strong>
                                    <div className="ledger-field-value">{data.customField4}</div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="ledger-action-buttons">
                                {data.balanceDue > 0 ? (
                                    <button
                                        onClick={() => setShowAddPaymentModal(true)}
                                        className="ledger-pay-btn"
                                    >
                                        <DollarSign size={16} /> Pay Due Amount
                                    </button>
                                ) : (
                                    <div className="ledger-paid-badge">
                                        <TrendingUp size={16} /> Paid
                                    </div>
                                )}
                                <button className="ledger-discount-btn">
                                    Add Discount
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Tabs Header ── */}
                <div className="ledger-tabs-header">
                    <div className="ledger-tabs-list">
                        {tabsList.map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`ledger-tab-btn ${activeTab === tab ? 'active' : ''}`}
                            >
                                {tab === 'Ledger' && <span className="tab-icon">📊</span>}
                                {tab === 'Sales' && <span className="tab-icon">📈</span>}
                                {tab === 'Payments' && <span className="tab-icon">💳</span>}
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Content Area ── */}
                <div className="ledger-content-area">

                    {/* Non-Ledger Tab Placeholders */}
                    {activeTab !== 'Ledger' && (
                        <div className="ledger-placeholder">
                            <div className="placeholder-emoji">🏗️</div>
                            <h3 className="placeholder-title">{activeTab} Module</h3>
                            <p className="placeholder-desc">The <strong>{activeTab}</strong> components will be integrated here in future phases.</p>
                        </div>
                    )}

                    {/* Ledger Tab Content */}
                    {activeTab === 'Ledger' && (
                        <div className="ledger-main-content">

                            {/* Controls Row (Spa-styled) */}
                            <div className="ledger-controls-row">
                                {/* Date Range */}
                                <div className="ledger-control-group date-range-group">
                                    <label className="control-label">Date Range</label>
                                    <div className="date-input-wrapper">
                                        <Calendar className="date-icon" size={16} />
                                        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="date-input" />
                                        <span className="date-separator">-</span>
                                        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="date-input" />
                                    </div>
                                </div>

                                {/* Format Selector - React Pill Segment Control */}
                                <div className="ledger-control-group format-selector-group">
                                    <label className="control-label">Ledger Format</label>
                                    <div className="format-pills">
                                        {['Format 1', 'Format 2', 'Format 3'].map(fmt => (
                                            <button
                                                key={fmt}
                                                onClick={() => setLedgerFormat(fmt)}
                                                className={`format-pill-btn ${ledgerFormat === fmt ? 'active' : ''}`}
                                            >
                                                {fmt}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Location & Actions */}
                                <div className="ledger-control-group location-actions-group">
                                    <label className="control-label">Business Location</label>
                                    <div className="location-actions-wrapper">
                                        <div className="location-select-wrapper">
                                            <MapPin className="location-icon" size={16} />
                                            <select value={location} onChange={e => setLocation(e.target.value)} className="location-select">
                                                <option value="all">All locations</option>
                                                <option value="main">Main Store</option>
                                            </select>
                                        </div>
                                        <button className="ledger-icon-btn">
                                            <Printer size={18} />
                                        </button>
                                        <button className="ledger-icon-btn">
                                            <Mail size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Store Name Right-Aligned (Subtle variant) */}
                            <div className="ledger-store-info">
                                <h4 className="store-name">{data.storeName}</h4>
                                <p className="store-address">
                                    {data.storeAddress}
                                </p>
                            </div>

                            {/* Customer & Account Summary Grid */}
                            <div className="ledger-summary-grid">

                                {/* Customer Column */}
                                <div className="summary-card customer-summary">
                                    <div className="card-header">
                                        To:
                                    </div>
                                    <div className="card-body">
                                        <strong className="summary-customer-name">{data.customer}</strong>
                                        <div className="summary-address">{data.address}</div>
                                        <div className="summary-contact-info"><strong>Email:</strong> {data.email}</div>
                                        <div className="summary-contact-info"><strong>Mobile:</strong> {data.mobile}</div>
                                        <div className="summary-contact-info"><strong>Tax number:</strong> {data.taxNumber}</div>
                                    </div>
                                </div>

                                {/* Account Summary Column */}
                                <div className="summary-card account-summary">
                                    <div className="card-header">
                                        <span>Account Summary</span>
                                        <span className="summary-date-range">{dateFrom} - {dateTo}</span>
                                    </div>
                                    <div className="card-body">
                                        <table className="summary-table">
                                            <tbody>
                                                <tr>
                                                    <td className="summary-label">Opening Balance</td>
                                                    <td className="summary-value">${data.openingBalance.toFixed(2)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="summary-label">Total Invoice</td>
                                                    <td className="summary-value">${data.totalInvoice.toFixed(2)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="summary-label">Total Paid</td>
                                                    <td className="summary-value paid">${data.totalPaid.toFixed(2)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="summary-label">Advance Balance</td>
                                                    <td className="summary-value">${data.advanceBalance.toFixed(2)}</td>
                                                </tr>
                                                <tr className="total-row">
                                                    <td className="summary-label">Balance due</td>
                                                    <td className="summary-value due">${data.balanceDue.toFixed(2)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {/* Divider Title */}
                            <div className="ledger-divider">
                                <span className="divider-badge">
                                    Showing Ledger Data
                                </span>
                            </div>

                            {/* Tables Container Based on Selected Format */}
                            <div className="ledger-tables-container">
                                {ledgerFormat === 'Format 1' && <Format1Table rows={data.detailedRows} />}
                                {ledgerFormat === 'Format 2' && <Format2Table rows={data.statementRows} onViewPayment={handleViewPayment} />}
                                {ledgerFormat === 'Format 3' && <Format3Table transactions={data.transactions} />}
                            </div>

                        </div>
                    )}
                </div>
            </div>

            {/* View Payment Sub-Modal */}
            {showViewPaymentsModal && (
                <ViewPaymentsModal
                    sale={sale}
                    transaction={selectedPaymentRow}
                    onClose={() => setShowViewPaymentsModal(false)}
                />
            )}

            {/* Add Payment Sub-Modal */}
            {showAddPaymentModal && (
                <AddPaymentModal
                    sale={selectedContact || { customer: data.customer }}
                    invoice={{
                        no: data.invoiceNo,
                        location: data.storeName,
                        amount: data.balanceDue.toFixed(2)
                    }}
                    onClose={() => setShowAddPaymentModal(false)}
                />
            )}
        </div>
    );
};

export default LedgerModal;
